/** @fileoverview Hook that shows a toast after an action runs: what it did, what its system writes did, how many people a send step reached, or that an export was sent. */

import { useCallback } from 'react';

import type {
  AppActionRunResult,
  AppActionWrite,
  FabrikAction,
  FabrikActionStep,
  FabrikExportTo,
} from '../../types/apps';
import { appToast } from './AppToaster';
import { useComponentStateStore } from './componentStateStore';

/** How long the toast stays visible before it closes itself. */
const TOAST_DURATION_MS = 4000;

/* Toast title of an export by where the file went; a download saves its own file and needs none. */
const EXPORT_TITLES: Partial<Record<FabrikExportTo['type'], string>> = {
  email: 'File sent',
  sharepoint: 'File saved to SharePoint',
};

/** What one run of an action reports back, for the toast. */
type RunCounts = Pick<AppActionRunResult, 'sent' | 'exports' | 'writes'>;

/**
 * Tells whether an action has at least one step of a kind.
 * @param actions actions of the definition
 * @param actionId id of the action
 * @param kind the step kind to look for
 */
export const actionHasStep = (
  actions: FabrikAction[],
  actionId: string,
  kind: FabrikAction['steps'][number]['kind']
): boolean =>
  Boolean(actions.find(({ id }) => id === actionId)?.steps.some((step) => step.kind === kind));

/** Write outcomes that worked; the others show as the error, see `writeFailureText`. */
const WORKED_OUTCOMES = new Set<AppActionWrite['outcome']>(['saved', 'pending_approval']);

/**
 * Tells whether a write worked: saved, or sent for approval.
 * @param write one write outcome of the run
 */
const writeWorked = ({ outcome }: AppActionWrite) => WORKED_OUTCOMES.has(outcome);

/** Shown for a write that did not work when the server gave no message. */
const WRITE_FAILED_SENTENCE = 'Not saved to SuccessFactors.';

/**
 * Builds one sentence per write that did not work (failed, unknown, skipped or not saved), from
 * the server's message, each sentence once: the steps rolled back by one failed save share one.
 * Empty when every write worked or the action has none.
 * @param writes write outcomes of the run
 */
export const writeFailureText = (writes: AppActionWrite[] = []): string =>
  [
    ...new Set(
      writes
        .filter((write) => !writeWorked(write))
        .map(({ message }) => message ?? WRITE_FAILED_SENTENCE)
    ),
  ].join(' ');

/**
 * Names what one step did, in two or three words, or nothing when the step needs no toast (a
 * download saves its own file) or did not reach anyone.
 * @param step one step of the action
 * @param result counts and write outcomes of the run
 */
const stepTitleOf = (step: FabrikActionStep, { sent, writes = [] }: RunCounts): string => {
  switch (step.kind) {
    case 'store': {
      const { rows } = step;
      if (rows.some((row) => 'text' in row)) {
        return 'Note added';
      }
      return rows.length === 1 ? 'Task added' : 'Tasks added';
    }
    case 'write':
      return writes.some(({ outcome }) => outcome === 'pending_approval')
        ? 'Sent for approval'
        : 'Saved';
    case 'send':
      return sent ? 'Email sent' : '';
    case 'export':
      return EXPORT_TITLES[step.to.type] ?? '';
    default:
      return '';
  }
};

/**
 * Says how many people a send step reached, for the toast description.
 * @param sent people the send step reached
 */
const reachTextOf = (sent?: number): string =>
  !sent ? '' : sent === 1 ? 'Sent to 1 person' : `Sent to ${sent} people`;

/**
 * Shows a short toast after an action ran without errors: the first step that did something
 * names it ("Tasks added", "Saved"), and the reach of a send step is the description. A send
 * step that reached no one, with nothing else done, shows a warning instead.
 */
export const useActionToast = () => {
  const { actions } = useComponentStateStore();

  /**
   * Shows the toast for one action run that worked, or nothing when the run has nothing to say.
   * @param actionId id of the action that just ran
   * @param result counts of the run: people a send step reached, exports sent by email, and
   *   the outcome of each write
   */
  const showActionToast = useCallback(
    (actionId: string, result: RunCounts) => {
      const steps = actions.find(({ id }) => id === actionId)?.steps ?? [];
      const title = steps.map((step) => stepTitleOf(step, result)).find(Boolean);
      if (title) {
        const description = reachTextOf(result.sent);
        appToast.add({
          type: 'success',
          title,
          description: description || undefined,
          timeout: TOAST_DURATION_MS,
        });
      } else if (result.sent === 0 && steps.some(({ kind }) => kind === 'send')) {
        appToast.add({ type: 'warning', title: 'No one to send to', timeout: TOAST_DURATION_MS });
      }
    },
    [actions]
  );

  return { showActionToast };
};
